import { useState, useEffect } from 'react';
import { userScopedStorageKey } from '../lib/userSession';

export interface AquariumSummary {
  id: string;
  name: string;
}

const STORAGE_KEY = 'reefguide:selectedAquariumId';

function readFromStorage(): string | null {
  try {
    return localStorage.getItem(userScopedStorageKey(STORAGE_KEY));
  } catch {
    return null;
  }
}

function writeToStorage(id: string | null): void {
  try {
    if (id === null) {
      localStorage.removeItem(userScopedStorageKey(STORAGE_KEY));
    } else {
      localStorage.setItem(userScopedStorageKey(STORAGE_KEY), id);
    }
  } catch {
    // localStorage may be unavailable (e.g. private browsing with storage disabled)
  }
}

export interface UseSelectedAquariumResult {
  selectedAquariumId: string | null;
  setSelectedAquariumId: (id: string | null) => void;
}

/**
 * Manages the selected aquarium ID with localStorage persistence.
 *
 * Behaviour:
 * - On mount, restores the previously stored ID if it still exists in the aquariums list.
 * - If only one aquarium exists and nothing is stored (or the stored ID is stale),
 *   auto-selects it.
 * - If the stored ID no longer matches any aquarium (e.g. aquarium was deleted),
 *   clears it and falls back to auto-select when only one remains.
 * - Exposes setSelectedAquariumId so callers can update selection; updates localStorage
 *   on every change.
 *
 * @param aquariums - The list of aquariums available to the user (may be undefined while loading).
 */
export function useSelectedAquarium(
  aquariums: AquariumSummary[] | undefined,
): UseSelectedAquariumResult {
  const [selectedAquariumId, setSelectedAquariumIdState] = useState<string | null>(() =>
    readFromStorage(),
  );

  useEffect(() => {
    // Wait until the aquarium list has loaded
    if (aquariums === undefined) {
      return;
    }

    const currentId = selectedAquariumId;

    // If the currently selected ID is no longer in the list, clear it
    if (currentId !== null) {
      const stillExists = aquariums.some((a) => a.id === currentId);
      if (!stillExists) {
        writeToStorage(null);
        setSelectedAquariumIdState(null);
        // After clearing, fall through to auto-select logic below if only one aquarium
        if (aquariums.length === 1) {
          const first = aquariums[0];
          if (first) {
            writeToStorage(first.id);
            setSelectedAquariumIdState(first.id);
          }
        }
      }
      // ID is valid — nothing to do
      return;
    }

    // No ID selected — auto-select if exactly one aquarium exists
    if (aquariums.length === 1) {
      const first = aquariums[0];
      if (first) {
        writeToStorage(first.id);
        setSelectedAquariumIdState(first.id);
      }
    }
  }, [aquariums, selectedAquariumId]);

  const setSelectedAquariumId = (id: string | null): void => {
    writeToStorage(id);
    setSelectedAquariumIdState(id);
  };

  return { selectedAquariumId, setSelectedAquariumId };
}
